import { DateTime } from 'luxon'

import { Button, DashboardModule, Text } from '@domain-group/suit-theme'

import ButtonWithOauth from '@components/ButtonWithOAuth'

import useIntegration from '@hooks/useIntegration'
import useIntegrations from '@hooks/useIntegrations'
import useCurrentUser from '@hooks/useCurrentUser'

import { confirmDelete } from '@functions/entity'

import type { IntegrationModel } from '@models/types'

import CanvaLogo from '@assets/Canva Icon logo.svg'

const CanvaIntegration = (props: { brandId: number }) => {
  const { brandId } = props
  const currentUserPayload = useCurrentUser()

  const integrationsPayload = useIntegrations({
    filters: { integrable_id: brandId, integrable_type: 'Brand', service_provider: 'canva' },
    performHttpRequests: !!brandId,
  })

  const {
    filteredIntegrations,
    hasFilteredIntegrations,
  } = integrationsPayload

  // Initial integration state and also the filters used to find the integration
  const initIntegrationState: Partial<IntegrationModel> = {
    integrable_type: 'Brand',
    service_provider: 'canva',
  }

  const integration = hasFilteredIntegrations ? filteredIntegrations[0] : initIntegrationState

  const integrationPayload = useIntegration(integration)

  const {
    callbacks: { deleteIntegration: deleteFn },
  } = integrationPayload

  const confirmDeleteParams = {
    callbacks: {
      deleteFn,
    },
    strings: {
      confirmString: 'Are you sure? This may affect design creation with Canva.',
      entityName: 'Integration',
    },
  }

  const { data } = integration
  const canvaIntegrated = !!integration?.id
  const connectedAccountId = data?.name || 'Unknown Account'
  const lastIntegratedAt = data?.last_integrated_at
    ? DateTime.fromISO(data.last_integrated_at).toFormat("DDD 'at' t a") : 'N/A'

  return (
    <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Canva">
      <DashboardModule.BlankState
        imageBoxProps={{ justifyContent: ['center'] }}
        imageUrl={CanvaLogo}
      >
        <DashboardModule.BlankState.Title>Connect Your Canva Account</DashboardModule.BlankState.Title>

        <DashboardModule.BlankState.Paragraph>
          This Canva integration allows you as a brand to sync your Canva brand templates to Template Manager
          which then allows you to permission the templates to providers, products, and agencies.

        </DashboardModule.BlankState.Paragraph>

        <DashboardModule.BlankState.Paragraph>
          Once the sync and permission process has been completed, agency users will be able to use Canva brand
          templates when creating an artwork and publish straight back to a Realhub order.
        </DashboardModule.BlankState.Paragraph>
      </DashboardModule.BlankState>

      <DashboardModule.Footer boxProps={{ flexDirection: ['column', 'row'] }}>
        <Text color="faintGrey" fontSize="xsmall" marginBottom={['large', 0]}>
          {canvaIntegrated
            ? `Connected by: ${connectedAccountId} on ${lastIntegratedAt}`
            : 'Click Connect to Canva'}
        </Text>

        <DashboardModule.FooterRight>
          { !canvaIntegrated && (
            <ButtonWithOauth
              authProviderKey="canva"
              buttonStyle="secondaryCreate"
              queryParams={{
                brand_id: brandId,
                current_user_id: currentUserPayload.currentUser.id,
                redirect: window.location.href,
              }}
              size="medium"
              // @ts-expect-error
              width="auto"
            >
              Connect with Canva
            </ButtonWithOauth>
          )}

          { canvaIntegrated && (
            <Button
              buttonStyle="secondaryDestroy"
              onClick={() => confirmDelete(confirmDeleteParams)}
              size="medium"
              // @ts-expect-error
              width="auto"
            >
              Delete Integration
            </Button>
          )}
        </DashboardModule.FooterRight>
      </DashboardModule.Footer>
    </DashboardModule>
  )
}

export default CanvaIntegration
